import React from 'react';

export enum VXLComponentId {
  ACTIVATED_OFFER_CARD = 'ACTIVATED_OFFER_CARD',
  AVATAR_CARD = 'AVATAR_CARD',
  BADGE = 'BADGE',
  BOOST_PROMO_CARD = 'BOOST_PROMO_CARD',
  BODY_SM_TEXT = 'BODY_SM_TEXT',
  BODY_XS_TEXT = 'BODY_XS_TEXT',
  BRAZE_CARD = 'BRAZE_CARD',
  BUTTON = 'BUTTON',
  CASHBACK_EARNED_CARD = 'CASHBACK_EARNED_CARD',
  CHECK_DEPOSIT_IMAGES_AND_AGREEMENT = 'CHECK_DEPOSIT_IMAGES_AND_AGREEMENT',
  CHECK_IMAGE_PREVIEW = 'CHECK_IMAGE_PREVIEW',
  COLUMN_TAX_CARD = 'COLUMN_TAX_CARD',
  CREDIT_SCORE_CARD = 'CREDIT_SCORE_CARD',
  DISCOVER_MORE_SECTION = 'DISCOVER_MORE_SECTION',
  DYNAMIC_MAP_CARD = 'DYNAMIC_MAP_CARD',
  EXTERNAL_ACCOUNTS_CARD = 'EXTERNAL_ACCOUNTS_CARD',
  FILTER_CHIP = 'FILTER_CHIP',
  HOME_ACTIVATED_OFFER_CARD = 'HOME_ACTIVATED_OFFER_CARD',
  HOME_OFFER_CARD = 'HOME_OFFER_CARD',
  HORIZONTAL_ACCOUNT_CARD = 'HORIZONTAL_ACCOUNT_CARD',
  HTML_COMPONENT = 'HTML_COMPONENT',
  ICON = 'ICON',
  ICON_AVATAR = 'ICON_AVATAR',
  INLINE_ALERT = 'INLINE_ALERT',
  LIST_ITEM = 'LIST_ITEM',
  LIST_ITEM_TEXT_STACK = 'LIST_ITEM_TEXT_STACK',
  MAPS_CARD = 'MAPS_CARD',
  MARKETING_CARD_CAROUSEL = 'MARKETING_CARD_CAROUSEL',
  MY_OFFERS_CARD = 'MY_OFFERS_CARD',
  OFFER_CARD = 'OFFER_CARD',
  ONBOARDING_CARD = 'ONBOARDING_CARD',
  ONBOARDING_CARD_CAROUSEL = 'ONBOARDING_CARD_CAROUSEL',
  PAGE_TITLE = 'PAGE_TITLE',
  PRESSABLE_SECTION_HEADER_V2 = 'PRESSABLE_SECTION_HEADER_V2',
  PRODUCT_OFFER_CARD = 'PRODUCT_OFFER_CARD',
  PRODUCT_OFFER_CARD_NON_ACTIONABLE = 'PRODUCT_OFFER_CARD_NON_ACTIONABLE',
  PROGRESS_CHECK_LIST = 'PROGRESS_CHECK_LIST',
  SECTION_DIVIDER = 'SECTION_DIVIDER',
  STACKED_TEXT_ACCESSORIES = 'STACKED_TEXT_ACCESSORIES',
  STANDARD_CARD = 'STANDARD_CARD',
  SWITCH = 'SWITCH',
  TAG = 'TAG',
  TRACKER = 'TRACKER',
  TRACKER_CARD = 'TRACKER_CARD',
  TRANSACTION_LIST_ITEM = 'TRANSACTION_LIST_ITEM',
  USER_AVATAR = 'USER_AVATAR',
  WALLET_ONBOARDING_CARD = 'WALLET_ONBOARDING_CARD',
  VXL_PROMO_CARD = 'VXL_PROMO_CARD',
  NEARBY_OFFERS_CAROUSEL = 'NEARBY_OFFERS_CAROUSEL',
}

export const isComponentId = (id: string): id is VXLComponentId => {
  return id in VXLComponentId;
};

export enum VXLGroupId {
  CATEGORY_GRID = 'CATEGORY_GRID',
  COMPONENT_GROUP = 'COMPONENT_GROUP',
  DYNAMIC_MAP_CARD = 'DYNAMIC_MAP_CARD',
  FEATURED_OFFER_CARD_CAROUSEL = 'FEATURED_OFFER_CARD_CAROUSEL',
  LIST_ITEM_GROUP = 'LIST_ITEM_GROUP',
  NEARBY_OFFERS_CAROUSEL = 'NEARBY_OFFERS_CAROUSEL',
  SCROLLING_BUTTON_GROUP = 'SCROLLING_BUTTON_GROUP',
  SIDE_BY_SIDE_CARD_GROUP = 'SIDE_BY_SIDE_CARD_GROUP',
  STANDARD_OFFER_CARD_CAROUSEL = 'STANDARD_OFFER_CARD_CAROUSEL',
}

export const isGroupId = (id: string): id is VXLGroupId => {
  return id in VXLGroupId;
};

export enum VXLSectionId {
  CARD_GROUP = 'CARD_GROUP',
}

export const isSectionId = (id: string): id is VXLSectionId => {
  return id in VXLSectionId;
};

export interface VXLNodeBase {
  props?: object;
  id: number | string;
}

export interface VXLReactNode extends VXLNodeBase {
  props?: never;
  render: () => React.ReactNode;
}

export const isVXLReactNode = (toRender: VXLNode): toRender is VXLReactNode => {
  return typeof toRender === 'object' && 'render' in toRender;
};

export interface VXLComponent extends VXLNodeBase {
  name: VXLComponentId;
}

export interface VXLGroup extends VXLNodeBase {
  name: VXLGroupId;
  props: object;
}

export interface VXLGenericSection extends VXLNodeBase {
  props: {
    childNodes: VXLChildNode[];

    [propsName: string]: any;
  };
}

export const isVXLGenericSectionNodeType = (
  toRender: VXLNode,
): toRender is VXLGenericSection => {
  return 'props' in toRender && 'childNodes' in (toRender.props ?? {});
};

export interface VXLSection extends VXLNodeBase {
  name: VXLSectionId;
  props: object;
}

// Nodes which can be rendered by a other nodes using the ChildNodeRenderer
export type VXLChildNode = VXLGroup | VXLComponent | VXLReactNode;

export const isChildNodeType = (
  toRender: VXLNode,
): toRender is VXLComponent | VXLGroup | VXLReactNode => {
  if (isVXLReactNode(toRender)) {
    return true;
  }
  return (
    typeof toRender === 'object' &&
    'name' in toRender &&
    (isComponentId(toRender.name) || isGroupId(toRender.name))
  );
};

export type VXLNode = VXLSection | VXLGenericSection | VXLChildNode;

export const isSectionNodeType = (
  toRender: VXLNode,
): toRender is VXLGenericSection | VXLSection => {
  return (
    isVXLGenericSectionNodeType(toRender) ||
    ('name' in toRender && isSectionId(toRender.name))
  );
};
